import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { API_BASE_URL } from '../config/api';
import { useTournament } from '../context/TournamentContext';

const API = API_BASE_URL;

// Matchup card component
function Matchup({ game, teamLookup, userPickIds, flip }) {
  if (!game) {
    return (
      <div className="matchup" style={styles.matchup}>
        <div style={styles.teamRow}><span style={styles.teamSeed}></span><span style={styles.teamName}>TBD</span></div>
        <div style={{ ...styles.teamRow, borderTop: '1px solid #d1d5db' }}><span style={styles.teamSeed}></span><span style={styles.teamName}>TBD</span></div>
      </div>
    );
  }

  const t1 = game.team1_id ? teamLookup[game.team1_id] : null;
  const t2 = game.team2_id ? teamLookup[game.team2_id] : null;
  const t1Picked = game.team1_id && userPickIds.has(game.team1_id);
  const t2Picked = game.team2_id && userPickIds.has(game.team2_id);
  const t1Won = game.completed && game.winner_id === game.team1_id;
  const t2Won = game.completed && game.winner_id === game.team2_id;
  const t1Elim = t1?.eliminated;
  const t2Elim = t2?.eliminated;

  const teamRowStyle = (picked, won, lost) => ({
    ...styles.teamRow,
    backgroundColor: picked ? '#d1fae5' : 'white',
    color: lost ? '#9ca3af' : 'inherit',
    fontWeight: won || picked ? '700' : '400',
    textDecoration: lost ? 'line-through' : 'none',
  });

  return (
    <div className="matchup" data-testid={`matchup-${game.game_id}`} style={styles.matchup}>
      <div style={{ ...teamRowStyle(t1Picked, t1Won, t2Won), borderRadius: '4px 4px 0 0' }}>
        <span style={styles.teamSeed}>{t1?.seed || ''}</span>
        <span style={{ ...styles.teamName, textAlign: flip ? 'right' : 'left', direction: flip ? 'rtl' : 'ltr' }}>{t1?.name || 'TBD'}</span>
        {game.completed && <span style={styles.score}>{game.team1_score ?? ''}</span>}
      </div>
      <div style={{ ...teamRowStyle(t2Picked, t2Won, t1Won), borderTop: '1px solid #d1d5db', borderRadius: '0 0 4px 4px' }}>
        <span style={styles.teamSeed}>{t2?.seed || ''}</span>
        <span style={{ ...styles.teamName, textAlign: flip ? 'right' : 'left', direction: flip ? 'rtl' : 'ltr' }}>{t2?.name || 'TBD'}</span>
        {game.completed && <span style={styles.score}>{game.team2_score ?? ''}</span>}
      </div>
    </div>
  );
}

// Renders one region's bracket (4 rounds: R1→R2→S16→E8)
function RegionBracket({ regionGames, teamLookup, userPickIds, regionName, flip }) {
  const rounds = [1, 2, 3, 4];
  const gamesByRound = {};
  
  // R1: sort by bracket_position (canonical order from initial setup)
  gamesByRound[1] = regionGames
    .filter(g => g.round === 1)
    .sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0));

  // For rounds 2-4: order games based on parent-child relationships, not bracket_position
  const expectedGames = { 1: 8, 2: 4, 3: 2, 4: 1 };
  
  for (let r = 2; r <= 4; r++) {
    const prevGames = gamesByRound[r - 1] || [];
    const realGames = regionGames.filter(g => g.round === r);
    const orderedRound = [];
    
    for (let i = 0; i < prevGames.length; i += 2) {
      const g1 = prevGames[i];
      const g2 = prevGames[i + 1];
      
      // Collect all team IDs from the feeder pair
      const feedTeams = new Set();
      if (g1) { if (g1.team1_id) feedTeams.add(g1.team1_id); if (g1.team2_id) feedTeams.add(g1.team2_id); if (g1.winner_id) feedTeams.add(g1.winner_id); }
      if (g2) { if (g2.team1_id) feedTeams.add(g2.team1_id); if (g2.team2_id) feedTeams.add(g2.team2_id); if (g2.winner_id) feedTeams.add(g2.winner_id); }
      
      // Find real game where ALL its teams come from this feeder pair
      const realMatch = realGames.find(g => {
        const gameTeams = [g.team1_id, g.team2_id].filter(Boolean);
        return gameTeams.length > 0 && gameTeams.every(t => feedTeams.has(t));
      });
      
      if (realMatch) {
        orderedRound.push(realMatch);
      } else {
        // No real game exists — build a virtual one from winners
        const t1 = g1?.completed && g1?.winner_id ? g1.winner_id : null;
        const t2 = g2?.completed && g2?.winner_id ? g2.winner_id : null;
        if (t1 || t2) {
          orderedRound.push({
            game_id: `virtual-${r}-${Math.floor(i / 2)}`,
            round: r,
            region: regionName,
            bracket_position: Math.floor(i / 2),
            team1_id: t1,
            team2_id: t2,
            completed: false,
            virtual: true
          });
        } else {
          orderedRound.push(null);
        }
      }
    }
    
    gamesByRound[r] = orderedRound;
  }

  const roundLabels = { 1: '1st Round', 2: '2nd Round', 3: 'Sweet 16', 4: 'Elite 8' };
  const roundOrder = flip ? [4, 3, 2, 1] : [1, 2, 3, 4];
  const SLOT_HEIGHT = 52; // Base height per R1 matchup slot

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{
        fontFamily: "'Bebas Neue', sans-serif",
        fontSize: '20px',
        color: '#064e3b',
        textAlign: 'center',
        padding: '6px 12px',
        backgroundColor: '#ecfdf5',
        borderRadius: '4px',
        marginBottom: '8px',
        letterSpacing: '2px'
      }}>
        {regionName}
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {roundOrder.map((roundNum) => {
          const games = gamesByRound[roundNum] || [];
          const expected = expectedGames[roundNum] || 1;
          const displayGames = [...games];
          while (displayGames.length < expected) {
            displayGames.push(null);
          }
          // Each round's slot height doubles: R1=1x, R2=2x, R3=4x, R4=8x
          const multiplier = Math.pow(2, roundNum - 1);
          const slotHeight = SLOT_HEIGHT * multiplier;
          return (
            <div key={roundNum} style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: '12px',
                color: '#6b7280',
                textAlign: 'center',
                padding: '2px 0',
                letterSpacing: '1px'
              }}>
                {roundLabels[roundNum]}
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0 4px',
              }}>
                {displayGames.map((game, idx) => (
                  <div key={game?.game_id || `tbd-${roundNum}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: `${slotHeight}px` }}>
                    <Matchup game={game} teamLookup={teamLookup} userPickIds={userPickIds} flip={flip} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Bracket() {
  const [loading, setLoading] = useState(true);
  const [bracket, setBracket] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewUserId = searchParams.get('user_id');
  const { selectedTournamentId } = useTournament();

  useEffect(() => {
    if (selectedTournamentId) {
      fetchBracket();
    }
  }, [selectedTournamentId, viewUserId]);

  const fetchBracket = async () => {
    try {
      setLoading(true);
      const bracketUrl = viewUserId 
        ? `${API}/bracket?tournament_id=${selectedTournamentId}&view_user_id=${viewUserId}`
        : `${API}/bracket?tournament_id=${selectedTournamentId}`;
      const [userRes, bracketRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(bracketUrl, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setBracket(bracketRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load bracket');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!bracket || !bracket.games || bracket.games.length === 0) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navigation user={user} />
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <h1 className="font-bebas text-4xl text-emerald-900 mb-4">No Bracket Available</h1>
          <p className="font-manrope text-gray-600">The tournament bracket hasn't been created yet.</p>
        </div>
      </div>
    );
  }

  const teamLookup = {};
  bracket.teams.forEach(t => { teamLookup[t.team_id] = t; });
  const userPickIds = new Set(bracket.user_picks.map(p => p.team_id));

  // Get region order from tournament semifinal config
  const semi1r1 = bracket.tournament.semi1_region1 || 'East';
  const semi1r2 = bracket.tournament.semi1_region2 || 'South';
  const semi2r1 = bracket.tournament.semi2_region1 || 'West';
  const semi2r2 = bracket.tournament.semi2_region2 || 'Midwest';

  // Left side regions and right side regions
  const leftRegions = [semi1r1, semi1r2];
  const rightRegions = [semi2r1, semi2r2];

  // Get games by region
  const getRegionGames = (region) => bracket.games.filter(g => g.region === region);

  // Find Elite 8 winner for a region (round 4 winner)
  const getElite8Winner = (region) => {
    const e8Game = bracket.games.find(g => g.region === region && g.round === 4 && g.completed && g.winner_id);
    return e8Game ? e8Game.winner_id : null;
  };

  // Real Final Four and Championship games from DB
  const realFinalFourGames = bracket.games
    .filter(g => g.round === 5)
    .sort((a, b) => (a.bracket_position || 0) - (b.bracket_position || 0));
  const realChampionshipGame = bracket.games.find(g => g.round === 6);

  // Build Final Four with virtual advancement from Elite 8 winners
  // Semi 1: semi1r1 winner vs semi1r2 winner  |  Semi 2: semi2r1 winner vs semi2r2 winner
  const semiFinals = [
    { r1: semi1r1, r2: semi1r2 },
    { r1: semi2r1, r2: semi2r2 },
  ];

  const finalFourGames = semiFinals.map((sf, idx) => {
    const t1 = getElite8Winner(sf.r1);
    const t2 = getElite8Winner(sf.r2);
    // Check if a real game exists for this semifinal
    const realGame = realFinalFourGames.find(g => {
      const gTeams = [g.team1_id, g.team2_id].filter(Boolean);
      return gTeams.length > 0 && gTeams.some(t => t === t1 || t === t2);
    }) || realFinalFourGames[idx];
    if (realGame) return realGame;
    if (t1 || t2) {
      return { game_id: `virtual-ff-${idx}`, round: 5, team1_id: t1, team2_id: t2, completed: false, virtual: true };
    }
    return null;
  });

  // Build Championship with virtual advancement from Final Four winners
  let championshipGame = realChampionshipGame;
  if (!championshipGame) {
    const ct1 = finalFourGames[0]?.completed ? finalFourGames[0]?.winner_id : null;
    const ct2 = finalFourGames[1]?.completed ? finalFourGames[1]?.winner_id : null;
    if (ct1 || ct2) {
      championshipGame = { game_id: 'virtual-champ', round: 6, team1_id: ct1, team2_id: ct2, completed: false, virtual: true };
    } else {
      championshipGame = null;
    }
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navigation user={user} />
      <div style={{ padding: '16px', maxWidth: '100%', overflowX: 'auto' }}>
        <div style={{ marginBottom: '16px' }}>
          <h1 className="font-bebas" style={{ fontSize: '28px', color: '#064e3b', marginBottom: '8px' }}>
            {bracket.view_user_name ? `${bracket.view_user_name}'s Bracket` : `${bracket.tournament.name || bracket.tournament.year} Bracket`}
          </h1>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-block', backgroundColor: '#d1fae5', padding: '4px 12px', borderRadius: '4px', fontSize: '13px', fontWeight: '700', fontFamily: "'Manrope', sans-serif" }}>
              Your Picks
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 'max-content' }}>
          {/* LEFT SIDE - 2 regions flowing left to right */}
          <div style={{ flex: 1 }}>
            {leftRegions.map(region => (
              <RegionBracket
                key={region}
                regionGames={getRegionGames(region)}
                teamLookup={teamLookup}
                userPickIds={userPickIds}
                regionName={region.toUpperCase()}
                flip={false}
              />
            ))}
          </div>

          {/* CENTER - Final Four + Championship */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0 8px', minWidth: '180px' }}>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '14px',
              color: '#6b7280',
              textAlign: 'center',
              letterSpacing: '1px',
              marginBottom: '4px'
            }}>
              Final Four
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
              {finalFourGames.map((game, idx) => (
                <Matchup key={game?.game_id || `ff-tbd-${idx}`} game={game} teamLookup={teamLookup} userPickIds={userPickIds} />
              ))}
            </div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '14px',
              color: '#6b7280',
              textAlign: 'center',
              letterSpacing: '1px',
              margin: '12px 0 4px'
            }}>
              Championship
            </div>
            <Matchup game={championshipGame || null} teamLookup={teamLookup} userPickIds={userPickIds} />
            {championshipGame?.completed && championshipGame?.winner_id && teamLookup[championshipGame.winner_id] && (
              <div data-testid="champion-banner" style={{
                marginTop: '12px',
                padding: '10px 20px',
                backgroundColor: '#065f46',
                borderRadius: '6px',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(6,95,70,0.3)',
              }}>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '12px', color: '#a7f3d0', letterSpacing: '2px' }}>CHAMPION</div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', color: '#ffffff', letterSpacing: '1px' }}>
                  {teamLookup[championshipGame.winner_id].name}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE - 2 regions flowing right to left */}
          <div style={{ flex: 1 }}>
            {rightRegions.map(region => (
              <RegionBracket
                key={region}
                regionGames={getRegionGames(region)}
                teamLookup={teamLookup}
                userPickIds={userPickIds}
                regionName={region.toUpperCase()}
                flip={true}
              />
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: '10px', color: '#9ca3af', marginTop: '8px' }} data-testid="bracket-version">v3-20260322</div>
      </div>
    </div>
  );
}

const styles = {
  matchup: {
    width: '160px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: 'white',
    fontSize: '11px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    margin: '2px 0',
  },
  teamRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 6px',
    gap: '4px',
    minHeight: '22px',
  },
  teamSeed: {
    fontWeight: '700',
    fontSize: '10px',
    color: '#6b7280',
    width: '16px',
    textAlign: 'center',
    flexShrink: 0,
  },
  teamName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'Manrope', sans-serif",
    fontSize: '11px',
  },
  score: {
    fontWeight: '700',
    fontSize: '11px',
    color: '#374151',
    flexShrink: 0,
  },
};
